import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

/** Community Learning Advisor applications from /partner-program: review, set status, keep notes. */

const STATUSES = [
  ["new", "New", "bg-orange-50 text-[#d15424]"],
  ["contacted", "Contacted", "bg-blue-50 text-blue-700"],
  ["approved", "Approved", "bg-green-50 text-green-700"],
  ["declined", "Declined", "bg-gray-100 text-gray-500"],
];
const STATUS = Object.fromEntries(STATUSES.map(([k, label, cls]) => [k, { label, cls }]));

function Detail({ label, value, href }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      {href ? (
        <a href={href} className="text-sm text-[#294a93] hover:underline break-all">{value}</a>
      ) : (
        <p className="text-sm text-gray-800 break-words">{value}</p>
      )}
    </div>
  );
}

function ApplicationRow({ item, onSaved, onDelete }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState(item.admin_note || "");
  const [saving, setSaving] = useState(false);
  const name = `${item.first_name} ${item.last_name}`.trim();
  const st = STATUS[item.status] || STATUS.new;
  const whatsapp = (item.phone || "").replace(/[^\d]/g, "");

  const save = (patch, done) => {
    setSaving(true);
    client
      .patch(`/admin/partner-applications/${item.id}`, patch)
      .then((res) => { onSaved(res.data); toast.success(done); })
      .catch(() => toast.error("Couldn't save that change."))
      .finally(() => setSaving(false));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{name}</span>
            <span className="text-xs text-gray-500">{item.city ? `${item.city}, ` : ""}{item.country}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{item.role}</span>
            <span className="text-xs bg-orange-50 text-[#d15424] px-2 py-0.5 rounded-full">Reach {item.reach}</span>
          </div>
          <span className="text-xs text-gray-300">{new Date(item.created_at).toLocaleString()}</span>
        </div>
        {!open && <p className="text-sm text-gray-500 mt-2 line-clamp-1">{item.plan}</p>}
      </button>

      {open && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Detail label="Email" value={item.email} href={`mailto:${item.email}`} />
            <Detail label="Phone" value={item.phone} href={`tel:${item.phone}`} />
            {whatsapp && <Detail label="WhatsApp" value="Open chat" href={`https://wa.me/${whatsapp}`} />}
            <Detail label="Organisation / community" value={item.organisation} />
            <Detail label="Heard about us from" value={item.heard_from} />
          </div>
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">How they'd reach and guide people</p>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed whitespace-pre-line">{item.plan}</p>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-[200px_1fr]">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Status</p>
              <select
                value={item.status}
                disabled={saving}
                onChange={(e) => save({ status: e.target.value }, "Status updated.")}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
              >
                {STATUSES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Internal note</p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Call notes, referral link sent, contact person…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
              />
              {note !== (item.admin_note || "") && (
                <button type="button" disabled={saving} onClick={() => save({ admin_note: note }, "Note saved.")} className="mt-1 px-3 py-1.5 rounded-lg bg-[#294a93] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50">
                  Save note
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {confirming ? (
              <>
                <button type="button" onClick={() => setConfirming(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={() => onDelete(item.id)} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700">Yes, delete</button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50">Delete</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPartnersPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback((p = 1) => {
    setLoading(true);
    client
      .get("/admin/partner-applications", { params: { search, status, page: p } })
      .then((res) => {
        setItems(res.data.data || []);
        setMeta(res.data);
        setPage(p);
      })
      .catch(() => toast.error("Failed to load partner applications."))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => { load(1); }, [load]);

  const handleSaved = (updated) => setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));

  const handleDelete = (id) => {
    client
      .delete(`/admin/partner-applications/${id}`)
      .then(() => { toast.success("Application deleted."); load(page); })
      .catch(() => toast.error("Couldn't delete that application."));
  };

  const counts = meta?.counts || {};

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Partner Applications</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Community Learning Advisor applications from the partner programme page. New ones are also emailed to info@e1xacademy.net.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {[["", "All"], ...STATUSES.map(([k, label]) => [k, label])].map(([k, label]) => {
          const n = k ? counts[k] || 0 : Object.values(counts).reduce((a, b) => a + b, 0);
          return (
            <button
              key={k || "all"}
              type="button"
              onClick={() => setStatus(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${status === k ? "bg-[#294a93] text-white border-[#294a93]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, email, country or organisation…"
        className="mb-5 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-full sm:w-80"
      />

      <div className="space-y-3">
        {loading ? (
          [1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <p className="text-4xl mb-3">🤝</p>
            <p className="text-sm font-medium text-gray-600">No partner applications {status ? `marked ${STATUS[status].label.toLowerCase()}` : "yet"}</p>
          </div>
        ) : (
          items.map((item) => <ApplicationRow key={item.id} item={item} onSaved={handleSaved} onDelete={handleDelete} />)
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
